import { useMemo, useSyncExternalStore } from 'react';

import { useActiveContextSnapshot } from '../../lib/context/useActiveContext';
import type { PortalAgendaCalendarEvent } from './PortalMonthlyAgendaCalendar';

export const AGENDA_STORAGE_KEY = 'giyus.portal.agenda.events.v1';
const CHANGE_EVENT = 'giyus:agenda-changed';
export type AgendaScope =
  { readonly kind: 'network' } | { readonly kind: 'tenant'; readonly code: string } | null;

function isAgendaEvent(value: unknown): value is PortalAgendaCalendarEvent {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    typeof value.id === 'string' &&
    'dateIso' in value &&
    typeof value.dateIso === 'string' &&
    'title' in value &&
    typeof value.title === 'string' &&
    'startTime' in value &&
    typeof value.startTime === 'string' &&
    'category' in value &&
    typeof value.category === 'string'
  );
}

function parseRecords(raw: string | null): unknown[] {
  if (!raw) return [];
  const parsed: unknown = JSON.parse(raw);
  if (!Array.isArray(parsed)) throw new Error('Invalid agenda storage');
  return parsed;
}

/** Historical unscoped records stay visible in the Holding; they are never attributed to an arbitrary tenant. */
export function belongsToAgendaScope(
  event: PortalAgendaCalendarEvent,
  scope: AgendaScope,
): boolean {
  return scope?.kind === 'network' || (scope?.kind === 'tenant' && event.tenantCode === scope.code);
}

export function readAgendaEvents(
  raw: string | null,
  scope: AgendaScope,
): PortalAgendaCalendarEvent[] {
  try {
    return parseRecords(raw)
      .filter(isAgendaEvent)
      .filter((event) => belongsToAgendaScope(event, scope));
  } catch {
    return [];
  }
}

/** Read immediately before each change: preserve other tabs, scopes and unknown legacy records. */
export function changeAgendaEvent(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  scope: AgendaScope,
  change: { readonly add: PortalAgendaCalendarEvent } | { readonly removeId: string },
): boolean {
  if (scope === null) return false;
  try {
    const records = parseRecords(storage.getItem(AGENDA_STORAGE_KEY));
    if ('add' in change) {
      if (records.some((value) => isAgendaEvent(value) && value.id === change.add.id)) return false;
      const event = {
        ...change.add,
        ...(scope.kind === 'tenant' ? { tenantCode: scope.code } : {}),
      };
      storage.setItem(AGENDA_STORAGE_KEY, JSON.stringify([...records, event]));
    } else {
      storage.setItem(
        AGENDA_STORAGE_KEY,
        JSON.stringify(
          records.filter(
            (value) =>
              !isAgendaEvent(value) ||
              value.id !== change.removeId ||
              !belongsToAgendaScope(value, scope),
          ),
        ),
      );
    }
    return true;
  } catch {
    // Refuse to overwrite unreadable state or report a quota failure as a successful save.
    return false;
  }
}

function snapshot(): string | null {
  try {
    return window.localStorage.getItem(AGENDA_STORAGE_KEY);
  } catch {
    return null;
  }
}
function subscribe(onChange: () => void): () => void {
  window.addEventListener('storage', onChange);
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => {
    window.removeEventListener('storage', onChange);
    window.removeEventListener(CHANGE_EVENT, onChange);
  };
}

export function useStoredAgendaEvents() {
  const { isNetworkScope, hasNetworkAccess, context } = useActiveContextSnapshot();
  const raw = useSyncExternalStore(subscribe, snapshot, () => null);
  const scope: AgendaScope = useMemo(
    () =>
      isNetworkScope
        ? hasNetworkAccess
          ? { kind: 'network' }
          : null
        : context
          ? { kind: 'tenant', code: context.code }
          : null,
    [isNetworkScope, hasNetworkAccess, context],
  );
  const events = useMemo(() => readAgendaEvents(raw, scope), [raw, scope]);
  const change = (value: Parameters<typeof changeAgendaEvent>[2]): boolean => {
    try {
      const saved = changeAgendaEvent(window.localStorage, scope, value);
      if (saved) window.dispatchEvent(new Event(CHANGE_EVENT));
      return saved;
    } catch {
      return false;
    }
  };
  return { events, change };
}
